import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Area,
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";

import { FilterBar } from "../components/FilterBar";
import { Info } from "../components/Info";
import { Panel } from "../components/Panel";
import { SalesPanel } from "../components/SalesPanel";
import { StatCard, type Delta } from "../components/StatCard";
import { api } from "../api/client";
import {
  joinDays,
  pointDelta,
  rangeMetrics,
  relativeDelta,
  weekdayProfile,
  type RangeMetrics,
} from "../lib/conversion";
import { placeKey, useFilters } from "../lib/filters";
import { scaleHours, scaleMetrics, scaleRows } from "../lib/staffPercent";
import { applyStaff, hoursWithoutStaff, withoutStaff } from "../lib/staff";
import {
  CURRENCY,
  WEEKDAY_NAMES,
  daysBetween,
  eachDay,
  formatCount,
  formatHour,
  formatMoney,
  formatPercent,
  formatRatio,
  longDay,
  shiftDate,
  shortDay,
} from "../lib/format";
import { chartSeries, ui } from "../lib/ui";
import { useAsync, type AsyncState } from "../lib/useAsync";
import type {
  BranchDailyPoint,
  BranchHourOfDayPoint,
  DateRange,
  HourOfDayPoint,
  SalesDailyPoint,
} from "../types";

/* Brand-derived chart palette; light/dark values live in index.css. Each
   measure keeps one hue everywhere it appears — a tile, a line, a bar. */
const SERIES = chartSeries;
const HUE = {
  entries: SERIES[0],
  exits: SERIES[1],
  transactions: SERIES[2],
  sales: SERIES[3],
  conversion: SERIES[4],
  demographics: SERIES[5],
} as const;
const TICK = { fontSize: 12, fill: "var(--muted)" };
const AXIS_LINE = { stroke: "var(--grid)" };
const PERCENT_TICK = (value: number) => `${Math.round(value * 100)}%`;

/** The daily chart reaches one day past each end of the selected range, so
 *  the range's own edges sit inside the line instead of ending it. */
const BUFFER_DAYS = 1;
/** Matches MAX_RANGE_DAYS in the analytics router — the buffer and the
 *  previous-period window must not push a near-maximal range over it. */
const MAX_RANGE_DAYS = 400;
/** Days with data each period needs before the tiles show a change. */
const MIN_DELTA_DAYS = 3;
/** The hub's one footfall figure: the mean of entries and exits, so a door
 *  that misses some crossings one way is evened out by the other. Taken
 *  after the staff switch and the staff percentage. Display only. */
function footTraffic(entries: number, exits: number): number;
function footTraffic(entries: number | null, exits: number | null): number | null;
function footTraffic(entries: number | null, exits: number | null): number | null {
  return entries === null || exits === null ? null : Math.round((entries + exits) / 2);
}

/** What every panel says on the hub until a branch row is added. */
const PLACEHOLDER = "Select a branch above to see its numbers.";
/** What the sales tiles say until an export covers the range. */
const NO_SALES = "No sales export on file for this range — attach one under Sales & conversion";

/** What each figure and chart means — the ⓘ tooltips. */
const ABOUT = {
  traffic: "The average of entries and exits over the selected days, summed over the selected stores: one count of visitors that evens out a camera missing crossings in one direction. Staff come off under Advanced: Staff Face ID Exclusion (off by default) takes out the people recognised as enrolled staff, then the Staff percentage discounts what is left. The hint says how much each took off.",
  conversion: "Walk-in receipts ÷ entries, only over days that have both processed footage and an uploaded sales export, capped at 100%. Delivery and call-centre orders never walked in, so they are not conversions.",
  orders: "Walk-in receipts (فورى / اجل billing types) from the uploaded POS exports over the range — the sales footfall converts into. Delivery and call-centre orders are in the hint.",
  avgBasket: "Per actual sale: walk-in net sales ÷ walk-in receipts, what a buyer spends. Per potential sale (the hint): walk-in net sales ÷ entries on days with both, what every visitor is worth, buyer or not (= avg basket × conversion).",
  salesPerVisitor: "Walk-in net sales ÷ entries on days that have both: what each person who walked in is worth, buyer or not. Equals avg basket × conversion.",
  walkIn: "Net sales rung up in the store (فورى / اجل billing types) — the sales that footfall converts into.",
  delivery: "Net sales of delivery, call-centre and online orders. Nobody walked in for these, so they sit outside conversion.",
  returns: "Refunds over the range, as a positive amount, and their share of walk-in plus delivery sales.",
  daily: "Entries (and exits) per day. The line breaks over days with no processed footage instead of dropping to zero. Drag the handles under the chart to zoom every panel to those days.",
  sales: "Footfall next to the uploaded POS exports of the selected stores. Attach or remove an export per store here.",
  visitors: "Entries and walk-in receipts per day on the same axis: how many came in against how many bought.",
  dailyConversion: "Walk-in receipts ÷ entries for each day that has both, capped at 100%. The dashed line is the range average.",
  channels: "The range's net sales split by channel: walk-in, delivery and call centre, and returns.",
  sideBySide: "Each compared store's totals over the range, one row per store, in the colours of its series.",
  peakHours: "Average entries per hour of the day over the days with data, in the store's local time. The highlighted bar is the busiest hour.",
  weekday: "Average entries per weekday over the days with data (Egypt's week starts on Saturday). Hover for that weekday's average conversion when sales are on file.",
  ranking: "Stores in the selection ordered by foot traffic (the average of entries and exits) over the range, top 15.",
  gender: "Share of entries estimated male or female; entries with no usable face are unknown.",
  age: "Entries by estimated age band. Only entries with a usable face are placed.",
} as const;

function bufferedRange(range: DateRange): DateRange {
  if (daysBetween(range.from, range.to) + 2 * BUFFER_DAYS > MAX_RANGE_DAYS) return range;
  return {
    from: shiftDate(range.from, -BUFFER_DAYS),
    to: shiftDate(range.to, BUFFER_DAYS),
  };
}

/** A store on the dashboard: its id, display name and series colour. */
interface Compared {
  id: string;
  name: string;
  color: string;
}

const AGE_BANDS = [
  { key: "age_0_17", label: "0–17" },
  { key: "age_18_29", label: "18–29" },
  { key: "age_30_44", label: "30–44" },
  { key: "age_45_plus", label: "45+" },
] as const;

/** Uniform loading / error / empty handling that keeps the previous chart
 *  on screen, dimmed, while a new range loads — no layout jump. */
function Loaded<T>({
  state,
  empty,
  emptyMessage = "No data for this range.",
  waiting = false,
  children,
}: {
  state: AsyncState<T>;
  empty?: boolean;
  emptyMessage?: string;
  /** Nothing has been asked for yet (no branch selected): a placeholder. */
  waiting?: boolean;
  children: ReactNode;
}) {
  if (waiting) return <p className={`${ui.notice} text-muted`}>{PLACEHOLDER}</p>;
  if (state.error) return <p className={`${ui.notice} ${ui.noticeError}`}>{state.error}</p>;
  if (state.loading && state.data === null)
    return <p className={`${ui.notice} text-muted`}>Loading…</p>;
  if (empty && !state.loading) return <p className={`${ui.notice} text-muted`}>{emptyMessage}</p>;
  return (
    <div className={`transition-opacity duration-200 ${state.loading ? "opacity-50" : ""}`}>
      {children}
    </div>
  );
}

type Formatter = (value: number) => string;

/** One tooltip for every chart: the heading, then every series at that X
 *  with a stroke of its colour and the value leading. */
function ChartTip({
  active,
  payload,
  label,
  heading,
  format,
}: TooltipProps<number, string> & {
  heading?: (label: string | number) => string;
  format?: Record<string, Formatter> | Formatter;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload.filter((p) => p.value !== null && p.value !== undefined);
  if (rows.length === 0) return null;
  const fmt = (key: string, value: number) => {
    const f = typeof format === "function" ? format : format?.[key];
    return f ? f(value) : formatCount(value);
  };
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-semibold text-text">
        {heading ? heading(label as string | number) : String(label)}
      </div>
      <ul className="flex flex-col gap-0.5">
        {rows.map((row) => (
          <li key={String(row.dataKey)} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-[3px] w-3 rounded-full"
              style={{ background: row.color ?? "var(--text)" }}
            />
            <span className="font-semibold tabular-nums text-text">
              {fmt(String(row.dataKey), Number(row.value))}
            </span>
            <span className="text-muted">{row.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A gradient fill for an area, keyed by its series colour. */
function AreaGradient({ id, color }: { id: string; color: string }) {
  return (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={color} stopOpacity={0.45} />
      <stop offset="100%" stopColor={color} stopOpacity={0.03} />
    </linearGradient>
  );
}

export function Dashboard() {
  const { range, setRange, place, includeDemo, excludeStaff, staffPercent, single, branches } =
    useFilters();

  // The rows: one series per selected store. Two or more is a comparison —
  // the daily and peak-hours charts show one series per store instead of
  // their sum and a table puts their totals side by side.
  const compared: Compared[] = place.branchIds.map((id, index) => ({
    id,
    name: branches.find((b) => b.id === id)?.name ?? id,
    color: SERIES[index % SERIES.length],
  }));
  const comparing = compared.length > 1;
  const selectedIds = compared.length > 0 ? compared.map((b) => b.id) : null;
  // On the hub nothing is summed until a branch row is added: every panel
  // shows a placeholder and no request goes out. A branch PC is pinned to
  // its store, so it always has a selection.
  const needsBranch = !single && compared.length === 0;
  // The hub reads entries and exits as one Foot traffic figure; a branch PC
  // (pinned to its store) keeps them apart.
  const hub = !single;

  // Every day-level series is read once over a wide window: the previous
  // period of the same length (for the tiles' deltas), the range, and a
  // day either side (so the line does not end at the range's edge). Near
  // the API's maximum range the previous period is dropped, not the range.
  const length = daysBetween(range.from, range.to) + 1;
  const previous: DateRange = {
    from: shiftDate(range.from, -length),
    to: shiftDate(range.from, -1),
  };
  const padded = bufferedRange(range);
  const withPrevious = daysBetween(previous.from, padded.to) <= MAX_RANGE_DAYS;
  const wide: DateRange = withPrevious ? { from: previous.from, to: padded.to } : padded;

  // A sales upload changes the series without changing the filters.
  const [salesNonce, setSalesNonce] = useState(0);
  const deps = [range.from, range.to, placeKey(place), includeDemo];

  const summary = useAsync(
    () => (needsBranch ? Promise.resolve(null) : api.summary(range, place, includeDemo)),
    deps,
  );
  const dailyByBranch = useAsync(
    () => (needsBranch ? Promise.resolve(null) : api.dailyByBranch(wide, place, includeDemo)),
    deps,
  );
  const sales = useAsync(
    () => (needsBranch ? Promise.resolve(null) : api.salesByBranch(wide, place, includeDemo)),
    [...deps, salesNonce],
  );
  const hourOfDay = useAsync(
    () =>
      comparing || needsBranch ? Promise.resolve(null) : api.hourOfDay(range, place, includeDemo),
    deps,
  );
  const hourOfDayByBranch = useAsync(
    () => (comparing ? api.hourOfDayByBranch(range, place, includeDemo) : Promise.resolve(null)),
    deps,
  );
  const byBranch = useAsync(
    () => (needsBranch ? Promise.resolve(null) : api.byBranch(range, place, includeDemo)),
    deps,
  );

  // The Staff toggle: every series arrives with everyone counted plus the
  // staff twins; with the toggle on, the twins come off here, once, before
  // anything is summed or plotted.
  // …then the staff percentage discounts what is left.
  const footfall: BranchDailyPoint[] = scaleRows(
    applyStaff(dailyByBranch.data ?? [], excludeStaff),
    staffPercent,
  );
  const branchTotals = scaleRows(applyStaff(byBranch.data ?? [], excludeStaff), staffPercent);
  const soldRows: SalesDailyPoint[] = sales.data ?? [];
  const rangeDays = eachDay(range.from, range.to);
  const paddedDays = eachDay(padded.from, padded.to);

  // Totals over the range and the period before it, for the tiles.
  const current = rangeMetrics(rangeDays, footfall, soldRows, selectedIds);
  const before = withPrevious
    ? rangeMetrics(eachDay(previous.from, previous.to), footfall, soldRows, selectedIds)
    : null;
  const hasSales = current.salesDays > 0;
  // Totals are compared **per day with data**: a period with three processed
  // days against one with ninety would otherwise read as a collapse. Rates
  // (conversion, basket) compare as they are. Fewer than MIN_DELTA_DAYS on
  // either side is too thin to compare at all.
  const hasPrevious =
    before !== null && before.footfallDays >= MIN_DELTA_DAYS && current.footfallDays >= MIN_DELTA_DAYS;
  const hasPreviousSales =
    hasPrevious && before.salesDays >= MIN_DELTA_DAYS && current.salesDays >= MIN_DELTA_DAYS;
  const deltaLabel = hasPrevious ? `vs previous ${length} days, per day with data` : undefined;
  const perDay = (total: number, days: number) => (days > 0 ? total / days : null);
  const pct = (cur: number | null, prev: number | null, ok = hasPrevious): Delta | null => {
    const value = ok ? relativeDelta(cur, prev) : null;
    return value === null ? null : { value, unit: "percent" };
  };
  const pts = (cur: number | null, prev: number | null, ok = hasPrevious): Delta | null => {
    const value = ok ? pointDelta(cur, prev) : null;
    return value === null ? null : { value, unit: "points" };
  };

  // The daily series (padded), summed over the selection, and the same kept
  // apart per compared store.
  const dailySeries = joinDays(paddedDays, footfall, soldRows, selectedIds).map((row) => ({
    ...row,
    traffic: footTraffic(row.entries, row.exits),
  }));
  const perBranch = compared.map((branch) => ({
    branch,
    rows: joinDays(paddedDays, footfall, soldRows, [branch.id]),
  }));
  const comparedDaily = paddedDays.map((day, index) => {
    const point: Record<string, string | number | null> = { day };
    for (const { branch, rows } of perBranch) {
      point[branch.id] = hub
        ? footTraffic(rows[index].entries, rows[index].exits)
        : rows[index].entries;
      point[`${branch.id}:conversion`] = rows[index].conversion;
    }
    return point;
  });
  const weekdays = weekdayProfile(joinDays(rangeDays, footfall, soldRows, selectedIds));
  const bestWeekday = weekdays.reduce(
    (best, point) =>
      point.avgEntries !== null && (best === null || point.avgEntries > (best.avgEntries ?? 0))
        ? point
        : best,
    null as (typeof weekdays)[number] | null,
  );

  // Dragging the brush under the daily chart narrows the whole dashboard to
  // the selected days — applied once the handles have rested, so every
  // panel reloads once, not per pixel.
  const brushTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(brushTimer.current), []);
  const onBrush = (window_: { startIndex?: number; endIndex?: number }) => {
    const start = window_.startIndex ?? 0;
    const end = window_.endIndex ?? dailySeries.length - 1;
    if (start === 0 && end === dailySeries.length - 1) return;
    if (end - start < 1) return;
    window.clearTimeout(brushTimer.current);
    brushTimer.current = window.setTimeout(() => {
      // The series is padded a day either side; a handle left on the pad
      // means "the edge", not a day outside the range.
      const from = dailySeries[start].day < range.from ? range.from : dailySeries[start].day;
      const to = dailySeries[end].day > range.to ? range.to : dailySeries[end].day;
      if (from !== range.from || to !== range.to) setRange({ from, to });
    }, 500);
  };
  const brush = (
    <Brush
      dataKey="day"
      height={26}
      travellerWidth={8}
      stroke="var(--accent-2)"
      fill="var(--bg)"
      tickFormatter={shortDay}
      onChange={onBrush}
    />
  );
  const chartKey = `${padded.from}|${padded.to}|${compared.map((b) => b.id).join(",")}`;

  const comparedHours = hoursByBranch(
    compared,
    scaleHours(hoursWithoutStaff(hourOfDayByBranch.data ?? [], excludeStaff), staffPercent),
  );
  const hours: HourOfDayPoint[] = scaleHours(
    hoursWithoutStaff(hourOfDay.data ?? [], excludeStaff),
    staffPercent,
  );
  const peakHour = hours.reduce(
    (best, point) => (best === null || point.avg_entries > best.avg_entries ? point : best),
    null as HourOfDayPoint | null,
  );
  const byBranchRows = branchTotals
    .map((row) => ({ ...row, label: row.name ?? row.branch_id, traffic: footTraffic(row.entries, row.exits) }))
    .sort((a, b) => b.traffic - a.traffic)
    .slice(0, 15);
  // The side-by-side table keeps the selection order (and colour), and a
  // compared store with nothing in the range still gets its row.
  const sideBySide = compared.map((branch) => ({
    ...branch,
    metrics: rangeMetrics(rangeDays, footfall, soldRows, [branch.id]),
    hours: branchTotals.find((row) => row.branch_id === branch.id)?.hours_covered ?? 0,
    female: branchTotals.find((row) => row.branch_id === branch.id)?.female ?? 0,
    male: branchTotals.find((row) => row.branch_id === branch.id)?.male ?? 0,
  }));

  // Same order as the series: face ID first, then the percentage of what is
  // left — so the tile can say how much each one took off.
  const counted = summary.data;
  const afterFaceId = counted ? (excludeStaff ? withoutStaff(counted) : counted) : null;
  const totals = afterFaceId ? scaleMetrics(afterFaceId, staffPercent) : null;
  const staffHint = (() => {
    if (!counted || !afterFaceId || !totals) return undefined;
    const all = footTraffic(counted.entries, counted.exits);
    const faceId = all - footTraffic(afterFaceId.entries, afterFaceId.exits);
    const percent =
      footTraffic(afterFaceId.entries, afterFaceId.exits) - footTraffic(totals.entries, totals.exits);
    if (!excludeStaff && staffPercent <= 0) return undefined;
    return [
      `${formatCount(all)} counted`,
      excludeStaff ? `−${formatCount(faceId)} by face ID` : null,
      staffPercent > 0 ? `−${formatCount(percent)} by ${staffPercent}%` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  })();
  const genderMix = totals
    ? [
        { name: "Male", value: totals.male },
        { name: "Female", value: totals.female },
        { name: "Unknown", value: totals.unknown_demo },
      ].filter((slice) => slice.value > 0)
    : [];
  const ageMix = totals
    ? AGE_BANDS.map((band) => ({ name: band.label, value: totals[band.key] }))
    : [];
  const acrossHint = needsBranch
    ? PLACEHOLDER
    : [comparing ? `across ${compared.length} stores` : null, staffHint]
        .filter(Boolean)
        .join(" · ") || undefined;

  return (
    <div className="flex flex-col gap-5">
      <FilterBar />

      <Loaded state={summary}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Foot traffic"
            value={totals ? footTraffic(totals.entries, totals.exits) : null}
            unit="people"
            about={ABOUT.traffic}
            color={HUE.entries}
            delta={pct(
              perDay(footTraffic(current.entries, current.exits), current.footfallDays),
              before ? perDay(footTraffic(before.entries, before.exits), before.footfallDays) : null,
            )}
            deltaLabel={deltaLabel}
            hint={acrossHint}
          />
          <StatCard
            label="Sales orders"
            value={needsBranch || !hasSales ? null : current.storeTransactions}
            unit="receipts"
            about={ABOUT.orders}
            color={HUE.transactions}
            delta={
              hasSales
                ? pct(
                    perDay(current.storeTransactions, current.salesDays),
                    before ? perDay(before.storeTransactions, before.salesDays) : null,
                    hasPreviousSales,
                  )
                : null
            }
            deltaLabel={deltaLabel}
            hint={
              needsBranch
                ? PLACEHOLDER
                : hasSales
                ? `Walk-in · ${formatCount(current.deliveryTransactions)} delivery orders besides · ${current.salesDays} days on file`
                : NO_SALES
            }
          />
          <StatCard
            label="Conversion"
            value={hasSales ? formatRatio(current.conversion) : null}
            unit="of visitors"
            about={ABOUT.conversion}
            color={HUE.conversion}
            delta={hasSales ? pts(current.conversion, before?.conversion ?? null, hasPreviousSales) : null}
            deltaLabel={deltaLabel}
            hint={
              needsBranch
                ? PLACEHOLDER
                : !hasSales
                ? NO_SALES
                : current.matchedDays > 0
                ? `${formatCount(current.matchedTransactions)} receipts ÷ ${formatCount(current.matchedEntries)} entries over ${current.matchedDays} matched days`
                : "No day has both footfall and sales yet"
            }
          />
          <StatCard
            label="Avg basket"
            value={hasSales ? formatMoney(current.avgBasket) : null}
            unit={`${CURRENCY} / sale`}
            about={ABOUT.avgBasket}
            color={HUE.sales}
            delta={hasSales ? pct(current.avgBasket, before?.avgBasket ?? null, hasPreviousSales) : null}
            deltaLabel={deltaLabel}
            hint={
              needsBranch
                ? PLACEHOLDER
                : !hasSales
                ? NO_SALES
                : `${formatMoney(current.salesPerVisitor)} ${CURRENCY} per visitor (potential sale)`
            }
          />
        </div>
      </Loaded>
      <Panel
        title="Daily footfall"
        about={ABOUT.daily}
        subtitle={
          (hub
            ? comparing
              ? "Foot traffic (average of entries and exits) per day for each store, with a day either side of the range for context. "
              : "Foot traffic (average of entries and exits) per day, with a day either side of the range for context. "
            : comparing
            ? "Entries per day for each store, with a day either side of the range for context. "
            : "Entries and exits per day, with a day either side of the range for context. ") +
          "Days with no processed footage break the line. Drag the handles below the chart to zoom every panel to those days."
        }
      >
        <Loaded state={dailyByBranch} empty={footfall.length === 0} waiting={needsBranch}>
          <ResponsiveContainer width="100%" height={320}>
            {comparing ? (
              <LineChart key={chartKey} data={comparedDaily} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--grid)" />
                <XAxis dataKey="day" tickFormatter={shortDay} tick={TICK} axisLine={AXIS_LINE} tickLine={false} minTickGap={24} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={<ChartTip heading={(l) => longDay(String(l))} />} cursor={{ stroke: "var(--muted)", strokeWidth: 1 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                {compared.map((branch) => (
                  <Line
                    key={branch.id}
                    type="monotone"
                    dataKey={branch.id}
                    name={branch.name}
                    stroke={branch.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--surface)" }}
                    connectNulls={false}
                  />
                ))}
                {brush}
              </LineChart>
            ) : (
              <ComposedChart key={chartKey} data={dailySeries} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <AreaGradient id="fill-entries" color={HUE.entries} />
                </defs>
                <CartesianGrid vertical={false} stroke="var(--grid)" />
                <XAxis dataKey="day" tickFormatter={shortDay} tick={TICK} axisLine={AXIS_LINE} tickLine={false} minTickGap={24} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={<ChartTip heading={(l) => longDay(String(l))} />} cursor={{ stroke: "var(--muted)", strokeWidth: 1 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey={hub ? "traffic" : "entries"}
                  name={hub ? "Foot traffic" : "Entries"}
                  stroke={HUE.entries}
                  strokeWidth={2}
                  fill="url(#fill-entries)"
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--surface)" }}
                  connectNulls={false}
                />
                {!hub && (
                  <Line
                    type="monotone"
                    dataKey="exits"
                    name="Exits"
                    stroke={HUE.exits}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--surface)" }}
                    connectNulls={false}
                  />
                )}
                {brush}
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </Loaded>
      </Panel>

      <Panel
        title="Sales & conversion"
        about={ABOUT.sales}
        subtitle="Attach each store's POS export (one row per day and billing type) to put transactions next to entries. Conversion = store transactions ÷ entries, on days that have both; delivery and call-centre orders are not conversions and returns are not sales."
      >
        <SalesPanel
          branches={single && compared.length === 0 ? [] : compared}
          range={range}
          onChanged={() => setSalesNonce((n) => n + 1)}
        />
        {hasSales && (
          <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-5">
            {!comparing && (
              <div>
                <h3 className="m-0 flex items-center gap-2 text-sm font-semibold">
                  Visitors and transactions
                  <Info text={ABOUT.visitors} />
                </h3>
                <p className="mt-0.5 mb-2 text-xs text-muted">Entries and store transactions per day</p>
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={dailySeries} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <AreaGradient id="fill-entries-2" color={HUE.entries} />
                    </defs>
                    <CartesianGrid vertical={false} stroke="var(--grid)" />
                    <XAxis dataKey="day" tickFormatter={shortDay} tick={TICK} axisLine={AXIS_LINE} tickLine={false} minTickGap={24} />
                    <YAxis tick={TICK} axisLine={false} tickLine={false} width={48} />
                    <Tooltip content={<ChartTip heading={(l) => longDay(String(l))} />} cursor={{ fill: "color-mix(in srgb, var(--accent) 6%, transparent)" }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="transactions" name="Store transactions" fill={HUE.transactions} radius={[4, 4, 0, 0]} maxBarSize={22} />
                    <Area
                      type="monotone"
                      dataKey="entries"
                      name="Entries"
                      stroke={HUE.entries}
                      strokeWidth={2}
                      fill="url(#fill-entries-2)"
                      dot={false}
                      connectNulls={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
            <div>
              <h3 className="m-0 flex items-center gap-2 text-sm font-semibold">
                Daily conversion rate
                <Info text={ABOUT.dailyConversion} />
              </h3>
              <p className="mt-0.5 mb-2 text-xs text-muted">
                {comparing
                  ? "Store transactions ÷ entries, per store"
                  : `Store transactions ÷ entries · range average ${formatRatio(current.conversion)}`}
              </p>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={comparing ? comparedDaily : dailySeries} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--grid)" />
                  <XAxis dataKey="day" tickFormatter={shortDay} tick={TICK} axisLine={AXIS_LINE} tickLine={false} minTickGap={24} />
                  <YAxis tick={TICK} axisLine={false} tickLine={false} width={48} tickFormatter={PERCENT_TICK} domain={[0, "auto"]} />
                  <Tooltip content={<ChartTip heading={(l) => longDay(String(l))} format={(v) => formatRatio(v)} />} cursor={{ stroke: "var(--muted)", strokeWidth: 1 }} />
                  {comparing && <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />}
                  {!comparing && current.conversion !== null && (
                    <ReferenceLine y={current.conversion} stroke={HUE.conversion} strokeDasharray="4 4" strokeOpacity={0.6} />
                  )}
                  {comparing ? (
                    compared.map((branch) => (
                      <Line
                        key={branch.id}
                        type="monotone"
                        dataKey={`${branch.id}:conversion`}
                        name={branch.name}
                        stroke={branch.color}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--surface)" }}
                        connectNulls={false}
                      />
                    ))
                  ) : (
                    <Line
                      type="monotone"
                      dataKey="conversion"
                      name="Conversion"
                      stroke={HUE.conversion}
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: HUE.conversion, strokeWidth: 0 }}
                      activeDot={{ r: 6, strokeWidth: 2, stroke: "var(--surface)" }}
                      connectNulls={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {hasSales && (
          <ChannelMix metrics={current} before={hasPreviousSales ? before : null} deltaLabel={deltaLabel} />
        )}
      </Panel>

      {comparing && (
        <Panel title="Side by side" about={ABOUT.sideBySide} subtitle="Each store's totals in the range">
          <Loaded state={byBranch}>
            <div className="overflow-x-auto">
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th>Store</th>
                    <th className="text-right!">Entries</th>
                    <th className="text-right!">Exits</th>
                    <th className="text-right!">Days processed</th>
                    {hasSales && (
                      <>
                        <th className="text-right!">Store transactions</th>
                        <th className="text-right!">Conversion</th>
                        <th className="text-right!">Net sales ({CURRENCY})</th>
                        <th className="text-right!">Avg basket ({CURRENCY})</th>
                      </>
                    )}
                    <th className="text-right!">Classified</th>
                    <th className="text-right!">Female</th>
                  </tr>
                </thead>
                <tbody>
                  {sideBySide.map((row) => {
                    const classified = row.male + row.female;
                    return (
                      <tr key={row.id}>
                        <td>
                          <span className="inline-flex items-center gap-2">
                            <span aria-hidden="true" className="inline-block size-2.5 rounded-full" style={{ background: row.color }} />
                            {row.name}
                          </span>
                        </td>
                        <td className="text-right tabular-nums">{formatCount(row.metrics.entries)}</td>
                        <td className="text-right tabular-nums">{formatCount(row.metrics.exits)}</td>
                        <td className="text-right tabular-nums">{row.metrics.footfallDays} / {rangeDays.length}</td>
                        {hasSales && (
                          <>
                            <td className="text-right tabular-nums">{row.metrics.salesDays > 0 ? formatCount(row.metrics.storeTransactions) : "—"}</td>
                            <td className="text-right tabular-nums">{formatRatio(row.metrics.conversion)}</td>
                            <td className="text-right tabular-nums">{row.metrics.salesDays > 0 ? formatMoney(row.metrics.net) : "—"}</td>
                            <td className="text-right tabular-nums">{formatMoney(row.metrics.avgBasket)}</td>
                          </>
                        )}
                        <td className="text-right tabular-nums">{formatPercent(classified, row.metrics.entries)}</td>
                        <td className="text-right tabular-nums">{formatPercent(row.female, classified)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Loaded>
        </Panel>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-5">
        <Panel
          title="Peak hours"
          about={ABOUT.peakHours}
          subtitle={
            comparing
              ? "Average entries by hour of day, per store"
              : peakHour
                ? `Average entries by hour of day · busiest at ${formatHour(peakHour.hour)}`
                : "Average entries by hour of day"
          }
        >
          {comparing ? (
            <Loaded state={hourOfDayByBranch} empty={hourOfDayByBranch.data?.length === 0}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={comparedHours} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barGap={2}>
                  <CartesianGrid vertical={false} stroke="var(--grid)" />
                  <XAxis dataKey="hour" tickFormatter={formatHour} tick={TICK} axisLine={AXIS_LINE} tickLine={false} />
                  <YAxis tick={TICK} axisLine={false} tickLine={false} width={40} />
                  <Tooltip content={<ChartTip heading={(l) => formatHour(Number(l))} format={(v) => v.toFixed(1)} />} cursor={{ fill: "color-mix(in srgb, var(--accent) 6%, transparent)" }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  {compared.map((branch) => (
                    <Bar key={branch.id} dataKey={branch.id} name={branch.name} fill={branch.color} radius={[3, 3, 0, 0]} maxBarSize={18} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Loaded>
          ) : (
            <Loaded state={hourOfDay} empty={hours.length === 0} waiting={needsBranch}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={hours} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--grid)" />
                  <XAxis dataKey="hour" tickFormatter={formatHour} tick={TICK} axisLine={AXIS_LINE} tickLine={false} />
                  <YAxis tick={TICK} axisLine={false} tickLine={false} width={40} />
                  <Tooltip content={<ChartTip heading={(l) => formatHour(Number(l))} format={(v) => v.toFixed(1)} />} cursor={{ fill: "color-mix(in srgb, var(--accent) 6%, transparent)" }} />
                  <Bar dataKey="avg_entries" name="Avg entries" fill={HUE.entries} radius={[4, 4, 0, 0]} maxBarSize={28}>
                    {hours.map((point) => (
                      <Cell key={point.hour} fill={peakHour && point.hour === peakHour.hour ? HUE.exits : HUE.entries} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Loaded>
          )}
        </Panel>

        <Panel
          title="Day of week"
          about={ABOUT.weekday}
          subtitle={
            bestWeekday
              ? `Average entries per weekday · ${WEEKDAY_NAMES[bestWeekday.weekday]} is the busiest${hasSales ? "; hover for conversion" : ""}`
              : "Average entries per weekday"
          }
        >
          <Loaded state={dailyByBranch} empty={weekdays.every((point) => point.avgEntries === null)} waiting={needsBranch}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={weekdays.map((point) => ({
                  ...point,
                  name: WEEKDAY_NAMES[point.weekday],
                  avgEntries: point.avgEntries === null ? null : Math.round(point.avgEntries),
                }))}
                margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
              >
                <CartesianGrid vertical={false} stroke="var(--grid)" />
                <XAxis dataKey="name" tick={TICK} axisLine={AXIS_LINE} tickLine={false} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} width={40} />
                <Tooltip
                  content={
                    <ChartTip
                      heading={(l) => String(l)}
                      format={{ avgEntries: (v) => formatCount(v), avgConversion: (v) => formatRatio(v) }}
                    />
                  }
                  cursor={{ fill: "color-mix(in srgb, var(--accent) 6%, transparent)" }}
                />
                <Bar dataKey="avgEntries" name="Avg entries" fill={HUE.entries} radius={[4, 4, 0, 0]} maxBarSize={36}>
                  {weekdays.map((point) => (
                    <Cell key={point.weekday} fill={bestWeekday && point.weekday === bestWeekday.weekday ? HUE.exits : HUE.entries} />
                  ))}
                </Bar>
                {hasSales && (
                  <Line dataKey="avgConversion" name="Avg conversion" stroke="transparent" dot={false} activeDot={false} legendType="none" />
                )}
              </BarChart>
            </ResponsiveContainer>
          </Loaded>
        </Panel>

        {/* One store ranks against nothing; the hub shows this for a place
            or a comparison. */}
        {!single && compared.length !== 1 && (
          <Panel
            title="Branch ranking"
            about={ABOUT.ranking}
            subtitle={comparing ? "Total foot traffic in range" : "Total foot traffic in range (top 15)"}
          >
            <Loaded state={byBranch} empty={byBranch.data?.length === 0} waiting={needsBranch}>
              <ResponsiveContainer width="100%" height={Math.max(200, 24 * byBranchRows.length + 40)}>
                <BarChart data={byBranchRows} layout="vertical" margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid horizontal={false} stroke="var(--grid)" />
                  <XAxis type="number" tick={TICK} axisLine={AXIS_LINE} tickLine={false} />
                  <YAxis type="category" dataKey="label" width={160} tick={TICK} axisLine={false} tickLine={false} />
                  <Tooltip
                    content={
                      <ChartTip
                        heading={(l) => {
                          const row = byBranchRows.find((r) => r.label === l);
                          return row ? `${l} — ${row.governorate ?? ""} › ${row.area ?? ""}` : String(l);
                        }}
                      />
                    }
                    cursor={{ fill: "color-mix(in srgb, var(--accent) 6%, transparent)" }}
                  />
                  <Bar dataKey="traffic" name="Foot traffic" fill={HUE.entries} radius={[0, 4, 4, 0]} maxBarSize={18}>
                    {byBranchRows.map((row) => (
                      <Cell
                        key={row.branch_id}
                        fill={compared.find((b) => b.id === row.branch_id)?.color ?? HUE.entries}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Loaded>
          </Panel>
        )}
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-5">
        <Panel
          title="Gender mix"
          about={ABOUT.gender}
          subtitle={comparing ? "Share of classified entries, all compared stores" : "Share of classified entries"}
        >
          <Loaded state={summary} empty={genderMix.length === 0} waiting={needsBranch}>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={genderMix} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={2} stroke="var(--surface)" strokeWidth={2}>
                  {genderMix.map((slice, index) => (
                    <Cell key={slice.name} fill={[HUE.entries, HUE.demographics, "var(--muted)"][index % 3]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTip heading={(l) => String(l)} />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Loaded>
        </Panel>

        <Panel
          title="Age bands"
          about={ABOUT.age}
          subtitle={comparing ? "Entries by estimated age bucket, all compared stores" : "Entries by estimated age bucket"}
        >
          <Loaded state={summary} empty={ageMix.every((band) => band.value === 0)} waiting={needsBranch}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={ageMix} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--grid)" />
                <XAxis dataKey="name" tick={TICK} axisLine={AXIS_LINE} tickLine={false} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={<ChartTip heading={(l) => `Age ${l}`} />} cursor={{ fill: "color-mix(in srgb, var(--accent) 6%, transparent)" }} />
                <Bar dataKey="value" name="Entries" fill={HUE.sales} radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {ageMix.map((band, index) => (
                    <Cell key={band.name} fill={HUE.sales} fillOpacity={0.45 + (index * 0.55) / Math.max(1, ageMix.length - 1)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Loaded>
        </Panel>
      </div>

      <p className="mt-1 text-xs text-muted">
        Every footfall figure here is an hourly aggregate; sales figures are the
        daily sums of the uploaded export. Track lifecycle metadata is available
        from each branch; raw appearance and face vectors are never returned by
        the API.
      </p>
    </div>
  );
}

/** The sales split by channel over the range: walk-in, delivery and
 *  returns — the part of the sales picture conversion leaves out. */
function ChannelMix({
  metrics,
  before,
  deltaLabel,
}: {
  metrics: RangeMetrics;
  before: RangeMetrics | null;
  deltaLabel?: string;
}) {
  const gross = metrics.storeNet + metrics.deliveryNet;
  // Per sales day, like the headline tiles.
  const pct = (cur: number, prev: number | undefined): Delta | null => {
    const value =
      before && prev !== undefined && metrics.salesDays > 0 && before.salesDays > 0
        ? relativeDelta(cur / metrics.salesDays, prev / before.salesDays)
        : null;
    return value === null ? null : { value, unit: "percent" };
  };
  return (
    <div className="mt-5 flex flex-col gap-3">
      <div>
        <h3 className="m-0 flex items-center gap-2 text-sm font-semibold">
          Sales by channel
          <Info text={ABOUT.channels} />
        </h3>
        <p className="mt-0.5 text-xs text-muted">Net over the range; delivery and call-centre orders never walked in, so they are not conversions</p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
        <StatCard
          label="Walk-in sales"
          value={formatMoney(metrics.storeNet, true)}
          unit={CURRENCY}
          about={ABOUT.walkIn}
          color={HUE.transactions}
          delta={pct(metrics.storeNet, before?.storeNet)}
          deltaLabel={deltaLabel}
          hint={`${formatCount(metrics.storeTransactions)} transactions · ${formatPercent(metrics.storeNet, gross)} of sales`}
        />
        <StatCard
          label="Delivery & call centre"
          value={formatMoney(metrics.deliveryNet, true)}
          unit={CURRENCY}
          about={ABOUT.delivery}
          color={HUE.sales}
          delta={pct(metrics.deliveryNet, before?.deliveryNet)}
          deltaLabel={deltaLabel}
          hint={`${formatCount(metrics.deliveryTransactions)} orders · ${formatPercent(metrics.deliveryNet, gross)} of sales`}
        />
        <StatCard
          label="Returns"
          value={formatMoney(Math.abs(metrics.returnNet), true)}
          unit={CURRENCY}
          about={ABOUT.returns}
          color="var(--danger)"
          delta={pct(Math.abs(metrics.returnNet), before ? Math.abs(before.returnNet) : undefined)}
          deltaLabel={deltaLabel}
          invert
          hint={`${formatCount(metrics.returnTransactions)} refunds · ${formatRatio(metrics.returnRate)} of sales`}
        />
      </div>
    </div>
  );
}

/** The comparison's peak-hours chart: every hour any compared store has
 *  data for, one average per store (missing = 0 so the bars stay grouped). */
function hoursByBranch(
  compared: Compared[],
  rows: BranchHourOfDayPoint[] | null,
): Record<string, number>[] {
  const byKey = new Map((rows ?? []).map((row) => [`${row.branch_id}|${row.hour}`, row]));
  const hours = [...new Set((rows ?? []).map((row) => row.hour))].sort((a, b) => a - b);
  return hours.map((hour) => {
    const point: Record<string, number> = { hour };
    for (const branch of compared) {
      point[branch.id] = byKey.get(`${branch.id}|${hour}`)?.avg_entries ?? 0;
    }
    return point;
  });
}
